import { FC } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  findBy,
  FormControl,
  FormHelperText,
  InputLabel,
  makeStyles,
  MenuItem,
  MuiStack,
  Select,
  TextField,
  Typography,
} from '@perk-ui/core'
import { addDays, addMonths, addWeeks, addYears, format } from 'date-fns'
import { useFormik } from 'formik'
import { matchSorter } from 'match-sorter'
// import { useQueryClient } from 'react-query'
import { useHistory, useRouteMatch } from 'react-router'
import * as yup from 'yup'

import { useSitesContext } from '../../../components/SitesProvider'
// import QueryKeys from '../../../features/query-hooks/QueryKeys'
import useCreateTreatment from '../../../features/query-hooks/useCreateTreatment'
import useMedicalTreatments from '../../../features/query-hooks/useMedicalTreatments'
import usePatientCases from '../../../features/query-hooks/usePatientCases'
import useProtocols, {
  Protocol,
} from '../../../features/query-hooks/useProtocols'
import useProviders from '../../../features/query-hooks/useProviders'
import useTreatmentProducts from '../../../features/query-hooks/useTreatmentProducts'
import useUpdateTreatment from '../../../features/query-hooks/useUpdateTreatment'
import { buildName } from '../../../utils/buildName'
import {
  treatmentHasChanged,
  TreatmentTypeNameCustomizations,
} from '../../PatientFlow/Treatment/Treatment'
import PatientSelfOnboardUrls, {
  PatientSelfOnboardParams,
} from '../PatientSelfOnboardUrls'

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '787px',
    width: '100%',
    height: '100%',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  form: {
    marginTop: theme.spacing(2),
    '& > *:not(:last-child)': {
      marginBottom: theme.spacing(2),
    },
    '& > .MuiDivider-root': {
      marginBottom: theme.spacing(3),
    },
  },
  loadingContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(6),
  },
  sectionTitle: {
    textAlign: 'center',
    fontFamily: 'DM Sans',
    fontSize: '28px',
    lineHeight: '24px',
  },
  goBackButton: {
    border: '1px solid #90CAF9', // Light blue border
    borderRadius: '8px', // Rounded corners
    color: '#42A5F5 !important', // Blue text color
    textColor: '#42A5F5', // Blue text color
    textTransform: 'none', // Prevents all-uppercase text
    // fontWeight: 500, // Semi-bold text
    backgroundColor: 'white', // White background
    '&:hover': {
      backgroundColor: '#E3F2FD', // Light blue background on hover
    },
  },
}))

export interface TreatmentProps {}

export interface TreatmentFormValues {
  siteLocationId?: string
  providerId: string
  medicalTreatmentId: string
  treatmentProductId?: string
  treatmentDate: string
  protocol?: Protocol
}
/**
 * TODO: Refactor this component to use Routes to handle its internal `pageState`
 */
const Treatment: FC<TreatmentProps> = () => {
  const classes = useStyles()
  const { push, goBack } = useHistory()
  // const queryClient = useQueryClient()
  const hasCaseData = useRouteMatch<PatientSelfOnboardParams>(
    PatientSelfOnboardUrls.addTreatment.match,
  )
  const { patientId, caseId } = hasCaseData?.params || {}

  const createTreatment = useCreateTreatment()
  const updateTreatment = useUpdateTreatment()
  const { data: providers = [] } = useProviders()
  const { currentSite } = useSitesContext()
  const { data: protocols } = useProtocols('TreatmentProtocol')
  const {
    data: patientCases = [],
    isLoading: isLoadingCases,
  } = usePatientCases(patientId)

  const {
    data: medicalTreatments = [],
    isLoading: isLoadingMedicalTreatments,
  } = useMedicalTreatments()
  const {
    data: stemwaveTreatmentProducts = [],
    isLoading: isLoadingStemwaveProducts,
  } = useTreatmentProducts(
    medicalTreatments.find((t) => t.slug === 'shockwave-therapy')?.id || '',
  )

  // const location = useLocation<{ from?: string }>()
  const patientCase = findBy('id', caseId, patientCases)
  const primaryTreatment = patientCase?.patientTreatments?.[0]
  const isContura = currentSite?.organization?.name === 'Contura'
  const isStemWave = currentSite?.organization?.name === 'StemWave'

  const isLoading =
    isLoadingCases || isLoadingMedicalTreatments || isLoadingStemwaveProducts

  const validationSchema = yup.object({
    providerId: yup.string().defined('A provider must be selected'),
    medicalTreatmentId: yup
      .string()
      .defined('A treatment type must be selected'),
    treatmentProductId: yup
      .string()
      .defined('A treatment product must be selected'),
  })

  const {
    values,
    errors,
    touched,
    isSubmitting,
    isValid,
    handleBlur,
    handleSubmit,
    setFieldValue,
  } = useFormik<TreatmentFormValues>({
    enableReinitialize: true,
    validationSchema,
    initialValues: primaryTreatment
      ? {
          ...primaryTreatment,
          treatmentDate: primaryTreatment.treatmentDate,
        }
      : {
          providerId: '',
          medicalTreatmentId: isStemWave
            ? medicalTreatments.find((t) => t.slug === 'shockwave-therapy')
                ?.id ||
              medicalTreatments[0]?.id ||
              ''
            : '', // Default to empty for Contura and everyone else (free choice)
          treatmentProductId: isStemWave
            ? stemwaveTreatmentProducts.find((t) => t.name === 'StemWave')
                ?.id ||
              stemwaveTreatmentProducts[0]?.id ||
              ''
            : '', // Default to empty for Contura and everyone else (free choice)
          siteLocationId: '',
          treatmentDate: format(new Date(), 'yyyy-MM-dd'),
        },
    onSubmit: async (values) => {
      if (!patientId || !caseId) return
      if (currentSite?.siteLocations.length === 1) {
        values.siteLocationId = currentSite.siteLocations[0].id
      }
      const mmddyyyyFormat = format(new Date(), 'MM-dd-yyyy')
      if (primaryTreatment && treatmentHasChanged(values, primaryTreatment)) {
        // Updating a Treatment
        await updateTreatment
          .mutateAsync({
            id: primaryTreatment.id,
            patientCaseId: caseId,
            ...values,
          })
          .then((result) => {
            if (result.id)
              push(PatientSelfOnboardUrls.survey.create(patientId, caseId))
          })
      } else if (!primaryTreatment) {
        await createTreatment
          .mutateAsync({
            patientCaseId: caseId,
            ...values,
          })
          .then((result) => {
            if (result.id) {
              // queryClient.invalidateQueries(
              //   QueryKeys.surveyResponseGroupsAlt({
              //     currentSiteId: currentSite?.id || '',
              //     patientId,
              //     patientCaseId: caseId,
              //   }),
              // )
              push(PatientSelfOnboardUrls.survey.create(patientId, caseId))
            }
          })
        if (values.protocol) {
          const protocolPrimaryTreatments = (
            (protocols || []).find((prot) => prot === values.protocol)
              ?.protocolTreatments || []
          ).filter((treat) => treat.order > 1)
          if (protocolPrimaryTreatments) {
            protocolPrimaryTreatments
              ?.sort((a, b) => a.order - b.order)
              .map(async (treatment) => {
                const protTreatment = availableMedicalTreatmentOptions.find(
                  (treat) => treat.id === treatment.medicalTreatmentId,
                )
                let unformattedTreatmentDate
                switch (treatment.periodType) {
                  case 'days':
                    unformattedTreatmentDate = addDays(
                      new Date(mmddyyyyFormat),
                      treatment.periodAmount,
                    )
                    break
                  case 'weeks':
                    unformattedTreatmentDate = addWeeks(
                      new Date(mmddyyyyFormat),
                      treatment.periodAmount,
                    )
                    break
                  case 'months':
                    unformattedTreatmentDate = addMonths(
                      new Date(mmddyyyyFormat),
                      treatment.periodAmount,
                    )
                    break
                  case 'years':
                    unformattedTreatmentDate = addYears(
                      new Date(mmddyyyyFormat),
                      treatment.periodAmount,
                    )
                    break

                  default:
                    unformattedTreatmentDate = addWeeks(
                      new Date(mmddyyyyFormat),
                      0,
                    )
                    break
                }
                if (protTreatment) {
                  await createTreatment.mutateAsync({
                    patientCaseId: caseId,
                    siteLocationId: treatment.siteLocationId || '',
                    providerId: treatment.staffId || values.providerId,
                    medicalTreatmentId: protTreatment.id,
                    treatmentProductId: treatment.treatmentProductId,
                    protocolId: values.protocol?.id,
                    treatmentDate: format(
                      unformattedTreatmentDate as Date,
                      'yyyy-MM-dd',
                    ),
                  })
                }
              })
          }
        }
      } else {
        // queryClient.invalidateQueries(
        //   QueryKeys.surveyResponseGroupsAlt({
        //     currentSiteId: currentSite?.id || '',
        //     patientId,
        //     patientCaseId: caseId,
        //   }),
        // )
        push(PatientSelfOnboardUrls.survey.create(patientId, caseId))
      }
    },
  })

  // Only disable fields for StemWave when they have values
  const shouldDisableFields =
    isStemWave && !!values.medicalTreatmentId && !!values.treatmentProductId

  const { data: treatmentProducts = [] } = useTreatmentProducts(
    values.medicalTreatmentId || '',
  )

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress />
      </div>
    )
  }

  const availableMedicalTreatmentOptions = medicalTreatments.filter(
    (treatment) => {
      // Priority 1: If study assigned, ONLY show study treatments (all orgs)
      if (patientCase?.study) {
        return patientCase?.study?.medicalTreatments
          .map((mt) => mt.id)
          .includes(treatment.id)
      }

      // Priority 2: If protocol selected, show protocol treatments
      if (values.protocol) {
        return (
          values.protocol.protocolTreatments
            ?.map((pt) => pt.medicalTreatmentId)
            .includes(treatment.id) || false
        )
      }

      // Priority 3: Show all non-study-only treatments
      return treatment.onlyForStudy == false
    },
  )

  const availableTreatmentProductOptions = treatmentProducts.filter(
    (product) => {
      // Priority 1: If study assigned, ONLY show study products (all orgs)
      // Exception: Specific StemWave study allows all products
      if (
        patientCase?.study &&
        patientCase?.study.slug !==
          'platelet-rich-plasma-and-the-effects-of-nsaids-on-pain-and-functional-scores-in-knee-osteoarthritis'
      ) {
        return patientCase?.study?.treatmentProducts
          .map((tp) => tp.id)
          .includes(product.id)
      }

      // Priority 2: If protocol selected, show protocol products
      if (values.protocol) {
        return (
          values.protocol.protocolTreatments?.some(
            (pt) => pt.treatmentProductId === product.id,
          ) || false
        )
      }

      // Priority 3: Show all non-study-only products
      return product.onlyForStudy == false
    },
  )
  return (
    <Box className={classes.root}>
      <Typography className={classes.sectionTitle}>
        <b>Treatment Details</b>
      </Typography>
      <form className={classes.form} onSubmit={handleSubmit}>
        {currentSite && currentSite.siteLocations.length > 1 && (
          <FormControl style={{ width: '100%' }} error={!!errors.providerId}>
            <InputLabel id="site-location-label" required>
              Clinic Location
            </InputLabel>
            <Select
              id="patient-flow-treatment-site-location"
              labelId="site-location-label"
              placeholder="Select"
              value={values.siteLocationId || ''}
              fullWidth
              required
              onChange={(event) => {
                setFieldValue('siteLocationId', event.target.value)
              }}
            >
              {currentSite?.siteLocations.map((location) => (
                <MenuItem key={location.id} value={location.id}>
                  {location.name}
                </MenuItem>
              ))}
            </Select>
            {errors.siteLocationId && (
              <FormHelperText error>{errors.siteLocationId}</FormHelperText>
            )}
          </FormControl>
        )}
        <FormControl style={{ width: '100%' }} error={!!errors.providerId}>
          <InputLabel id="provider-label" required>
            Provider
          </InputLabel>
          <Select
            id="patient-flow-treatment-provider"
            labelId="provider-label"
            placeholder="Select"
            value={values.providerId}
            fullWidth
            required
            onChange={(event) => {
              setFieldValue('providerId', event.target.value)
            }}
          >
            {providers.map((prov) => (
              <MenuItem key={prov.id} value={prov.id}>
                {buildName(prov)}
              </MenuItem>
            ))}
          </Select>
          {errors.providerId && (
            <FormHelperText error>{errors.providerId}</FormHelperText>
          )}
        </FormControl>
        {(protocols || []).length > 0 &&
          !isContura &&
          patientCase?.patientTreatments?.length === 0 && (
            <Autocomplete
              id="patient-flow-treatment-protocol"
              options={protocols || []}
              getOptionLabel={(opt) => opt.name}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              filterOptions={(options, { inputValue }) =>
                inputValue
                  ? matchSorter(options, inputValue, { keys: ['name'] })
                  : options
              }
              value={values.protocol || null}
              fullWidth
              onChange={(_event, data) => {
                setFieldValue('protocol', data || null)
                const protocolPrimaryTreatment = (
                  (protocols || []).find((prot) => prot.id === data?.id)
                    ?.protocolTreatments || []
                ).find((treat) => treat.order === 1)
                if (protocolPrimaryTreatment) {
                  const protocolTreatment = availableMedicalTreatmentOptions.find(
                    (treat) =>
                      treat.id === protocolPrimaryTreatment.medicalTreatmentId,
                  )
                  const protocolProvider = providers.find(
                    (staff) => staff.id === protocolPrimaryTreatment?.staffId,
                  )
                  const protocolSiteLocation = currentSite?.siteLocations.find(
                    (location) =>
                      location.id === protocolPrimaryTreatment?.siteLocationId,
                  )
                  if (protocolPrimaryTreatment.id)
                    setFieldValue(
                      'medicalTreatmentId',
                      protocolTreatment?.id || '',
                    )
                  if (protocolProvider)
                    setFieldValue('providerId', protocolProvider?.id)
                  if (protocolSiteLocation)
                    setFieldValue('siteLocationId', protocolSiteLocation?.id)
                } else {
                  setFieldValue('medicalTreatmentId', '')
                  setFieldValue('treatmentProductId', '')
                  setFieldValue('providerId', '')
                  setFieldValue('siteLocationId', '')
                }
              }}
              renderInput={(params) => {
                // @ts-expect-error Mui's autocomplete types here are incomplete
                params.InputLabelProps.shrink = true
                return <TextField {...params} label="Protocol" />
              }}
            />
          )}
        <Autocomplete
          id="patient-flow-treatment-medical-treatment"
          options={availableMedicalTreatmentOptions}
          getOptionLabel={(opt) =>
            opt.name in TreatmentTypeNameCustomizations
              ? TreatmentTypeNameCustomizations[opt.name]
              : opt.name
          }
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          filterOptions={(options, { inputValue }) =>
            inputValue
              ? matchSorter(options, inputValue, {
                  keys: [
                    'name',
                    (opt) => TreatmentTypeNameCustomizations[opt.name],
                  ],
                })
              : options
          }
          disabled={shouldDisableFields}
          value={
            availableMedicalTreatmentOptions.find(
              (treatment) => treatment.id === values.medicalTreatmentId,
            ) ?? null
          }
          onChange={(_event, treatment) => {
            setFieldValue('medicalTreatmentId', treatment?.id || '')
          }}
          renderInput={(params) => {
            // @ts-expect-error Mui's autocomplete types here are incomplete
            params.InputLabelProps.shrink = true
            return (
              <TextField
                {...params}
                label="Treatment Type"
                name="medicalTreatmentId"
                required
                value={values.medicalTreatmentId}
                onBlur={handleBlur}
                error={
                  touched.medicalTreatmentId &&
                  Boolean(errors.medicalTreatmentId)
                }
                helperText={
                  touched.medicalTreatmentId && errors.medicalTreatmentId
                }
              />
            )
          }}
        />
        <Autocomplete
          id="patient-flow-treatment-product"
          options={availableTreatmentProductOptions}
          getOptionLabel={(opt) => opt.name}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
          filterOptions={(options, { inputValue }) =>
            inputValue
              ? matchSorter(options, inputValue, { keys: ['name'] })
              : options
          }
          disabled={shouldDisableFields}
          value={
            treatmentProducts.find(
              (prod) => prod.id === values.treatmentProductId,
            ) ?? null
          }
          onChange={(_event, prov) => {
            setFieldValue('treatmentProductId', prov?.id || '')
          }}
          renderInput={(params) => {
            // @ts-expect-error Mui's autocomplete types here are incomplete
            params.InputLabelProps.shrink = true
            return (
              <TextField
                {...params}
                name="treatmentProductId"
                label="Product Name"
                onBlur={handleBlur}
                required
                value={values.treatmentProductId}
                error={
                  touched.treatmentProductId &&
                  Boolean(errors.treatmentProductId)
                }
                helperText={
                  touched.treatmentProductId && errors.treatmentProductId
                }
              />
            )
          }}
        />
        <MuiStack
          direction="row"
          justifyContent="center"
          sx={{ marginTop: '40px', marginBottom: '40px' }}
        >
          <Button
            className={classes.goBackButton}
            disableElevation
            fullWidth
            style={{ marginRight: '20px' }}
            onClick={goBack}
          >
            Go Back
          </Button>
          {/* TODO: change submit form to validation and change step */}
          <Button
            fullWidth
            type="submit"
            loading={isSubmitting}
            disabled={!isValid}
          >
            Continue
          </Button>
        </MuiStack>
      </form>
    </Box>
  )
}

export default Treatment
